// Author: Ahsan Saleem
/**
 * Bloom Launcher — opened from the Bloom mark. The centre of the system: every
 * application (grouped Operate / Observe / Analyse / System), the objects you
 * touched recently, quick actions and search. Only functional destinations.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Search, NotebookPen, FlaskConical, MapPin, Sprout, TriangleAlert, Wrench } from 'lucide-react';
import { Bloom } from './Bloom.js';
import { Glyph } from './glyphs.js';
import { useSystem } from './system.js';
import { visibleWorkspaces, hasCap } from '../session.js';

export function Launcher() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { recents, user } = useSystem();
  const inputRef = useRef<HTMLInputElement>(null);
  const workspaces = useMemo(() => visibleWorkspaces(user?.capabilities), [user?.capabilities]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('bloomos-open-launcher', onOpen);
    return () => window.removeEventListener('bloomos-open-launcher', onOpen);
  }, []);
  useEffect(() => { if (open) setQ(''); }, [open]);

  const go = (to: string) => { setOpen(false); navigate(to); };

  const apps = useMemo(() => workspaces.flatMap((w) => w.apps.map((a) => ({ ...a, ws: w.label }))), [workspaces]);
  const caps = user?.capabilities;
  const actions = [
    hasCap(caps, 'ops.write') && { id: 'a-record', label: 'Record an observation', icon: NotebookPen, run: () => go('/v2/record') },
    import.meta.env.DEV && hasCap(caps, 'admin') && { id: 'a-sim', label: 'Open Simulation Console', icon: FlaskConical, run: () => go('/simulation') },
    { id: 'a-alerts', label: 'Review active alerts', icon: TriangleAlert, run: () => go('/v2/alerts') },
  ].filter(Boolean) as Array<{ id: string; label: string; icon: typeof NotebookPen; run: () => void }>;
  const filter = q.trim().toLowerCase();
  const fApps = filter ? apps.filter((a) => (a.label + ' ' + a.ws).toLowerCase().includes(filter)) : apps;
  const fActions = filter ? actions.filter((a) => a.label.toLowerCase().includes(filter)) : actions;

  const recentIcon = (k: string) => (k === 'zone' ? MapPin : k === 'batch' ? Sprout : k === 'asset' ? Wrench : TriangleAlert);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="launcher-overlay" />
        <Dialog.Content className="launcher" aria-describedby={undefined} onOpenAutoFocus={(e) => { e.preventDefault(); inputRef.current?.focus(); }}>
          <div className="launcher-head">
            <Bloom size={30} state="idle" />
            <Dialog.Title className="launcher-title">Bloom Launcher</Dialog.Title>
            <div className="launcher-search">
              <Search size={16} aria-hidden />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search applications, objects, actions…" aria-label="Search" />
            </div>
          </div>

          <div className="launcher-body">
            {!filter ? (
              workspaces.map((w) => (
                <section key={w.id} className="launcher-ws">
                  <div className="label">{w.label}</div>
                  <div className="launcher-apps">
                    {w.apps.map((a) => (
                      <button key={a.to} className="launcher-app" onClick={() => go(a.to)}>
                        <span className="launcher-app-ic"><Glyph name={a.glyph} size={22} /></span>
                        <span>{a.label}</span>
                      </button>
                    ))}
                  </div>
                </section>
              ))
            ) : (
              <section className="launcher-ws">
                <div className="label">Applications</div>
                <div className="launcher-apps">
                  {fApps.map((a) => (
                    <button key={a.to} className="launcher-app" onClick={() => go(a.to)}>
                      <span className="launcher-app-ic"><Glyph name={a.glyph} size={22} /></span>
                      <span>{a.label}</span>
                    </button>
                  ))}
                  {fApps.length === 0 && <div className="launcher-empty">No application matches</div>}
                </div>
              </section>
            )}

            {recents.length > 0 && !filter && (
              <section className="launcher-ws">
                <div className="label">Recent</div>
                <div className="launcher-recents">
                  {recents.map((r) => {
                    const Icon = recentIcon(r.kind);
                    return (
                      <button key={r.kind + r.id} className="launcher-recent" onClick={() => go(r.path)}>
                        <Icon size={14} aria-hidden /><span className="launcher-recent-l">{r.label}</span>
                        {r.sub && <span className="launcher-recent-s">{r.sub}</span>}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="launcher-ws">
              <div className="label">Quick actions</div>
              <div className="launcher-actions">
                {fActions.map((a) => {
                  const Icon = a.icon;
                  return <button key={a.id} className="launcher-action" onClick={a.run}><Icon size={16} aria-hidden /> {a.label}</button>;
                })}
              </div>
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
